import {
  CheckIcon,
  CopyIcon,
  GiftIcon,
  LinkSimpleIcon,
  ShareNetworkIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useEffect, useState } from "react";

type ReferralLinkCardProps = {
  isEligible: boolean;
  referralLink: string | null;
};

export const ReferralLinkCard = ({ isEligible, referralLink }: ReferralLinkCardProps) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 2200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  const copyLink = async () => {
    if (!referralLink) {
      return;
    }

    await navigator.clipboard.writeText(referralLink);

    setCopied(true);
  };

  const shareLink = async () => {
    if (!referralLink) {
      return;
    }

    if (navigator.share) {
      await navigator.share({
        title: "Découvrez Mon CV Pro",
        text: "Créez facilement votre CV professionnel avec Mon CV Pro.",
        url: referralLink,
      });

      return;
    }

    await copyLink();
  };

  return (
    <article
      className={cn(
        "grid gap-4 rounded-2xl border border-slate-200",
        "bg-white p-4 shadow-sm",
        "lg:grid-cols-[minmax(250px,0.85fr)_minmax(0,1.25fr)]",
        "lg:items-center lg:px-5",
      )}
    >
      <div className="flex items-center gap-3.5">
        <span
          className={cn(
            "grid size-14 shrink-0 place-items-center",
            "rounded-2xl bg-gradient-to-br",
            "from-emerald-50 to-indigo-100",
            "text-indigo-600",
          )}
        >
          <span className="relative">
            <LinkSimpleIcon aria-hidden size={29} weight="duotone" />

            <GiftIcon
              aria-hidden
              size={18}
              weight="fill"
              className="absolute -bottom-2 -right-3 text-emerald-500"
            />
          </span>
        </span>

        <div className="min-w-0">
          <h2 className="text-sm font-bold text-indigo-950">Votre lien de parrainage</h2>

          <p className="mt-1 text-[11px] leading-4 text-slate-600 sm:text-xs">
            {isEligible
              ? "Partagez ce lien pour inviter de nouveaux filleuls."
              : "Activez une offre SMART pour générer et partager votre lien personnel."}
          </p>
        </div>
      </div>

      <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
        <div
          className={cn(
            "flex min-h-11 min-w-0 items-center gap-2",
            "rounded-xl border px-3",
            isEligible && referralLink
              ? "border-slate-200 bg-slate-50 text-indigo-950"
              : "border-slate-200 bg-slate-50 text-slate-400",
          )}
        >
          <LinkSimpleIcon aria-hidden size={18} className="shrink-0" />

          <span className="truncate text-xs font-medium">
            {referralLink ?? "Lien indisponible"}
          </span>
        </div>

        <button
          type="button"
          disabled={!referralLink}
          onClick={() => {
            void copyLink();
          }}
          className={cn(
            "flex min-h-11 items-center justify-center gap-2",
            "rounded-xl border px-4 text-xs font-semibold",
            "transition-colors",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500",
            referralLink
              ? "border-indigo-600 bg-white text-indigo-700 hover:bg-indigo-50"
              : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400",
          )}
        >
          {copied ? (
            <CheckIcon aria-hidden size={17} weight="bold" />
          ) : (
            <CopyIcon aria-hidden size={17} />
          )}

          {copied ? "Copié" : "Copier"}
        </button>

        <button
          type="button"
          disabled={!referralLink}
          onClick={() => {
            void shareLink();
          }}
          className={cn(
            "flex min-h-11 items-center justify-center gap-2",
            "rounded-xl px-4 text-xs font-semibold",
            "transition-colors",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500",
            referralLink
              ? "bg-indigo-700 text-white hover:bg-indigo-800"
              : "cursor-not-allowed bg-slate-100 text-slate-400",
          )}
        >
          <ShareNetworkIcon aria-hidden size={17} />
          Partager
        </button>
      </div>
    </article>
  );
};
